
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Template and config actions
import { fetchAllTemplates } from './redux/actions/templateActions';
import { fetchGlobalConfig } from './redux/actions/globalConfigActions';

// RTK slices
import { setToken, resetInitialization, setInitializing, setInitialized } from './redux/slices/appSlice';
import { fetchAllModels } from './redux/slices/modelsSlice';
import { fetchAllAssessments, initializeAssessmentLocal } from './redux/slices/assessmentsSlice';
import { fetchAssessmentRisks } from './redux/slices/risksSlice';
// Security actions for loading scenarios (needed for RiskLandscape security charts)
import { fetchScenarios } from './redux/slices/securitySlice';
// Control actions for loading saved controls (needed for RiskLandscape policy coverage chart)
import { setSavedControls } from './redux/actions/controlActions';

import { Route, Routes } from 'react-router-dom';
import { AppShell, Transition } from '@mantine/core';
import ExplorerAutopilotFlow from './explorerMode/ExplorerAutopilotFlow';
import BuilderAssessmentFlow from './builderMode/BuilderAssessmentFlow';
import ModelInventory from './ModelInventory.js';
import RiskLandscape from './RiskLandscape';
// RisksBySource removed - now rendered in RiskLandscape table view
import RiskReports from './RiskReports';
import RiskAssessments from './RiskAssessmentsMantine';
import { useAuth0 } from '@auth0/auth0-react';
import EnhancedLoadingScreen from './components/EnhancedLoadingScreen.js';
import UniversalFileUploader from './UniversalFileUploader.jsx';
import {jwtDecode} from 'jwt-decode';
import ExplorerAssessmentCanvas from './canvas/ExplorerAssessmentCanvas';
import BuilderAssessmentCanvas from './canvas/BuilderAssessmentCanvas';
import { isRiskManager } from './utils/roleHelpers';
import { Header } from './components/Header';
import TemplateManagerHome from './templateManager/TemplateManagerHome';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import TemplateEditor from './templateManager/TemplateEditor';
import ModelFormConfigEditor from './globalConfigs/ModelFormConfigEditor';
import PromptEditorPage from './pages/PromptEditor';
import AgentManagerPage from './pages/AgentManager';
import useInactivityTimeout from './hooks/useInactivityTimeout';
import './App.css';

function App() {
  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently} = useAuth0();

  // New RTK selectors (primary source)
  const modelsLoading = useSelector(state => state.models.loading);
  const assessmentsLoading = useSelector(state => state.assessments.loading?.all);
  const appInitializing = useSelector(state => state.app.isInitializing);

  const [userRoles, setUserRoles] = useState([]);
  const [fadeIn, setFadeIn] = useState(false);
  // Only show full animated loading screen during INITIAL app load (appInitializing)
  // Subsequent data refreshes (modelsLoading/assessmentsLoading) should not trigger the animated screen
  const showInitialLoadingScreen = isLoading || appInitializing;
  console.log('[App] Loading states:', { isLoading, modelsLoading, assessmentsLoading, appInitializing, showInitialLoadingScreen });
  const userIsRiskManager = useMemo(() => isRiskManager(userRoles), [userRoles]);
  const dispatch = useDispatch();
  useInactivityTimeout();

  // Use new RTK models state as the single source of truth
  const modelsById = useSelector((state) => state.models.byId);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Reset initialization state to ensure loading screen shows
        // even if redux-persist hydrated old state with isInitialized=true
        dispatch(resetInitialization());
        dispatch(setInitializing(true));

        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'read:models write:models delete:models'
          }
        });

        console.log('Token obtained, initializing app...');

        // Store token in Redux for RTK thunks to use
        dispatch(setToken(token));

        // Parallel data fetches using RTK thunks - single source of truth
        // Templates and config are non-blocking - Supabase free tier hibernates causing fetch failures
        // Templates ARE required for creating assessments, but not for viewing existing data
        const [, assessmentsResult] = await Promise.all([
          dispatch(fetchAllModels()),       // RTK thunk (uses token from state)
          dispatch(fetchAllAssessments()),  // RTK thunk
          dispatch(fetchAllTemplates({ publishedOnly: true })).catch(err => {
            console.warn('[App] Templates unavailable (Supabase may be hibernating). You can view existing data, but cannot create new assessments until templates load.');
            return { payload: [] };
          }),
          dispatch(fetchGlobalConfig('legacy_risk_themes')).catch(err => {
            console.warn('[App] Legacy risk themes unavailable (non-critical, templates have impact_categories)');
            return { payload: null };
          }),
        ]);

        // Initialize state.risks.byAssessment for each assessment so RiskLandscape works
        // This loads saved risks into modelRisks.areaRisks
        const assessments = assessmentsResult?.payload || [];
        console.log('[App] Initializing risk state for', assessments.length, 'assessments');
        await Promise.all(assessments.map(async (assessment) => {
          const modelId = assessment.model_uuid;
          const assessmentUuid = assessment.assessment_uuid;
          if (assessmentUuid) {
            try {
              // Initialize assessment in RTK assessmentsSlice (now MERGES, doesn't overwrite)
              dispatch(
                initializeAssessmentLocal({
                  assessmentUuid,
                  modelId,
                  modelName: assessment.model_name,
                  assessmentName: assessment.assessment_name || assessment.model_name,
                  assessmentStatus: assessment.assessment_status,
                })
              );
              // Fetch saved risks into risksSlice via RTK async thunk
              await dispatch(fetchAssessmentRisks({ assessmentUuid, modelId }));
              // Fetch security scenarios for security charts in RiskLandscape
              await dispatch(fetchScenarios({ modelId, assessmentUuid }));
              // Fetch saved controls for policy coverage chart in RiskLandscape
              await dispatch(setSavedControls(assessmentUuid, modelId));
            } catch (err) {
              console.warn(`[App] Failed to load data for assessment ${assessmentUuid}:`, err);
            }
          }
        }));
        console.log('[App] Risk, security, and controls state initialized');

        dispatch(setInitialized(true));
      } catch (error) {
        console.error('Error initializing app:', error);
        // Still mark as initialized so the loading screen dismisses —
        // partial data is better than an infinite loading screen
        dispatch(setInitialized(true));
        if (error.error === 'login_required' || error.error === 'consent_required') {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      }
    };

    if (isAuthenticated) {
      initializeApp();
    }
  }, [dispatch, getAccessTokenSilently, isAuthenticated, logout]);

  const hasNotStarted = Object.values(modelsById).some(
    (model) => (model.assessment_status || '').toLowerCase() === 'not started'
  );

  const hasPendingReviews = Object.values(modelsById).some(
    (model) => model.review_status === 'submitted'
  );

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai', 
            scope: 'openid profile email read:models write:models delete:models' 
          }
        });

        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        setUserRoles(roles);
        console.log('User roles:', roles);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };

    if (isAuthenticated) {
      fetchRoles();
    }
  }, [isAuthenticated, getAccessTokenSilently]);





  useEffect(() => {
    if (!showInitialLoadingScreen) {
      setFadeIn(true);
    }
  }, [showInitialLoadingScreen]);

  // Only show the animated loading screen during initial app load
  // Subsequent data refreshes (e.g., after creating assessment) should not show this
  if (showInitialLoadingScreen) {
    return (
      <EnhancedLoadingScreen
        onComplete={() => {
          // Ensure we stop showing the loading screen immediately after chat completes
          // by flipping modelsLoading false if the fetch already finished.
          // If still loading, fallback to state change from reducer.
        }}
      />
    );
  }

  return (
    <div className="App">
    <AppShell
      header={{ height: 68 }}
      padding="md"
      styles={{
        main: { backgroundColor: '#F5F5F5' }
      }}
    >
      <AppShell.Header>
        <Header
          user={user}
          isAuthenticated={isAuthenticated}
          userRoles={userRoles}
          hasNotStarted={hasNotStarted}
          hasPendingReviews={hasPendingReviews}
          userIsRiskManager={userIsRiskManager}
          onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />
      </AppShell.Header>

      <AppShell.Main>
        <Transition mounted={fadeIn} transition="fade" duration={800} timingFunction="ease">
          {(styles) => (
            <div style={styles}>
              <Routes>
        <Route path="/" element={<RiskLandscape />} />
        <Route path="/risk-assessments" element={<RiskAssessments />} />
        <Route path="/model-inventory" element={<ModelInventory />} />
        {/* REMOVED - ModelDetailsModal route deleted as part of legacy cleanup */}
          {/* Route removed - RisksBySource now in RiskLandscape table view */}
          <Route path="/risk-reports" element={<RiskReports />} />
          {/* Legacy route redirect for backward compatibility */}
          <Route path="/risks-by-model" element={<RiskReports />} />
          <Route path="/builder-assessment/:modelId/:assessmentUuid" element={<BuilderAssessmentFlow/>} />
          <Route path="/builder-report/:modelId/:assessmentUuid" element={<BuilderAssessmentCanvas/>} />
          <Route path="/explorer-assessment/:modelId/:assessmentUuid" element={<ExplorerAutopilotFlow/>} />
          <Route path="/explorer-report/:modelId/:assessmentUuid" element={<ExplorerAssessmentCanvas/>} />
          <Route path="/universal-uploader" element={<UniversalFileUploader />} />
          {/* REMOVED - AdminConfigHome route deleted as part of legacy config cleanup */}
          <Route path="/template-manager" element={<TemplateManagerHome />} />
          <Route path="/skills" element={<TemplateManagerHome />} />
          <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="/template-editor/:templateId" element={<TemplateEditor />} />
          <Route path="/model-form-config" element={<ModelFormConfigEditor />} />
          <Route path="/prompts" element={<PromptEditorPage />} />
          <Route path="/agents" element={<AgentManagerPage />} />
          <Route path="*" element={<div>Not Found</div>} />
          </Routes>
            </div>
          )}
        </Transition>
      </AppShell.Main>
    </AppShell>
    </div>

  );
}

export default App;
